import { GeoJsonLayer } from "@deck.gl/layers";
import { TileLayer } from "@deck.gl/geo-layers";
import hexRgb from "hex-rgb";

import { randomColor } from "..";
import { aoiBasinMap } from "../../config/config";
import { iconConfig } from "../../config/config.icons";

export const createTileOverlay = (overlay, renderSubLayers) => {
  if (overlay.layers?.length > 0) {
    return new TileLayer({
      id: overlay.id,
      getTileData: (tile) => {
        // handle zxy tile format
        if (overlay.xyz) {
          return (
            overlay.baseUrl +
            `/${tile.index?.z}/${tile.index?.y}/${tile.index?.x}`
          );
        }
        let url_bbox;

        const { west, north, east, south } = tile.bbox;
        url_bbox = `&bbox=${west},${south},${east},${north}`;

        return (
          overlay.baseUrl +
          "?" +
          Object.keys(overlay.queryParams)
            .map((key) => key + "=" + overlay.queryParams[key])
            .join("&") +
          `&layers=show:${overlay.layers.join(",")}` +
          url_bbox
        );
      },

      opacity: overlay.opacity,
      pickable: true,
      renderSubLayers,
      tileSize: overlay.tileSize || 512,
    });
  }
};

export function getNewLayersAsync({
  aoiState,
  dataState,
  dispatch,
  getData,
  filteredSitesData,
  handlePointLayerEvent,
  layers,
  layerActions,
}) {
  // initialize empty list to hold promises
  let newLayerPromises = [];
  Object.keys(layers).forEach((id) => {
    if (layers[id].showLayer) {
      // get data from store, or fetch if necessary
      newLayerPromises.push(
        new Promise((resolve, reject) => {
          dispatch(
            getData({
              dataId: layers[id].data,
              source: "GetNewLayersAsync",
              bypassRefresh: true, // don't refetch
            })
          )
            .unwrap()
            .then((getDataPromiseResult) => {
              let filteredData =
                getDataPromiseResult.filteredData || getDataPromiseResult.data;
              
              if (filteredSitesData !== null && filteredSitesData !== undefined) {
                filteredData = filteredSitesData;
              }

              switch (id) {
                case "ngwos-project-sites":
                  resolve(
                    createNgwosProjectSitesLayer({
                      aoiState,
                      dataState,
                      dispatch,
                      getData,
                      filteredData,
                      handlePointLayerEvent,
                      layer: layers[id],
                      layerActions,
                    })
                  );
                  break;
                default:
                  // initialize new layer
                  let newLayer = undefined;

                  const colorValue = layers[id].colorValue;

                  const colorByProperty = layers[id].properties.find(
                    (p) => colorValue === p.id
                  );

                  // update point count
                  dispatch(
                    layerActions.updatePointCount({
                      id,
                      count: filteredData.length,
                    })
                  );

                  // Build color map
                  //
                  // Check for values of `colorValue` property without an assigned color
                  // and add a random color if needed
                  let colorMap = colorByProperty.colorMap;
                  let uniquePropertyValues = [
                    ...new Set(
                      filteredData
                        .map((feat) => {
                          if (Array.isArray(feat.properties[colorValue])) {
                            return feat.properties[colorValue].map((f) => f);
                          } else {
                            return feat.properties[colorValue];
                          }
                        })
                        .flat()
                        .filter((val) => val !== undefined)
                    ),
                  ];
                  const unassignedValues = uniquePropertyValues.filter(
                    (val) => !Object.keys(colorMap).includes(val)
                  );
                  if (unassignedValues.length > 0) {
                    unassignedValues.forEach((item) => {
                      colorMap = {
                        ...colorMap,
                        [item]: { hex: randomColor(), opacity: 0.8 },
                      };
                    });

                    // Update colorMap state
                    dispatch(
                      layerActions.updateColorMap({
                        layerId: id,
                        propertyId: colorByProperty.id,
                        colorMap,
                      })
                    );
                  }

                  // define function to pass to GeoJsonLayer
                  const getColors = (feature) => {
                    const colorValue = layers[id].colorValue;
                    let featureValue = feature.properties[colorValue];
                    let hexColor, rbgArray;

                    // Handle case where featureValue is array
                    if (Array.isArray(featureValue)) {
                      // CASE: single value
                      if (featureValue.length === 1) {
                        featureValue = featureValue[0];
                        hexColor = colorMap[featureValue].hex;
                        rbgArray = hexRgb(hexColor, { format: "array" });
                        rbgArray[3] = Math.floor(
                          colorMap[featureValue].opacity * 255
                        );
                      }
                      // CASE: multiple values
                      else if (featureValue.length > 1) {
                        const multipleValuesMap =
                          colorByProperty.hasOwnProperty(
                            "arrayMultipleValuesColor"
                          )
                            ? colorByProperty.arrayMultipleValuesColor
                            : null;
                        if (multipleValuesMap) {
                          hexColor = multipleValuesMap.hex;
                          rbgArray = hexRgb(hexColor, { format: "array" });
                          rbgArray[3] = Math.floor(
                            multipleValuesMap.opacity * 255
                          );
                        } else {
                          hexColor = "#808080";
                          rbgArray = hexRgb(hexColor, { format: "array" });
                          rbgArray[3] = Math.floor(0.8 * 255);
                        }
                      }
                      // CASE: no data
                      else {
                        hexColor = "#808080";
                        rbgArray = hexRgb(hexColor, { format: "array" });
                        rbgArray[3] = Math.floor(0.8 * 255);
                      }
                    }
                    // if not array, featureValue type assumed to be string
                    else {
                      hexColor = colorMap[featureValue].hex;
                      rbgArray = hexRgb(hexColor, { format: "array" });
                      rbgArray[3] = Math.floor(
                        colorMap[featureValue].opacity * 255
                      );
                    }
                    return rbgArray;
                  };

                  // Parameterize layer
                  //
                  // Parameters shared across all layer types
                  const sharedLayerParams = {
                    id: id,
                    data: filteredData,
                    extruded: true,
                    filled: true,
                    getElevation: 30,
                    stroked: false,
                    pickable: true,
                    onHover: (info, event) =>
                      handlePointLayerEvent(info, event, id, "hover"),
                    onClick: (info, event) =>
                      handlePointLayerEvent(info, event, id, "click"),
                    updateTriggers: {
                      filterData: aoiState.selection.id,
                    },
                  };

                  // Point layer specific parameters
                  if (layers[id].layerType === "point") {
                    const pointLayerParams = {
                      pointType: "circle",
                      getFillColor: getColors,
                      getPointRadius: 4,
                      pointRadiusMinPixels: 3,
                      pointRadiusUnits: "pixels",
                      updateTriggers: {
                        ...sharedLayerParams.updateTriggers,
                        getFillColor: layers[id].colorValue,
                      },
                    };

                    newLayer = new GeoJsonLayer({
                      ...sharedLayerParams,
                      ...pointLayerParams,
                    });
                  }

                  // Icon layer specific parameters
                  if (layers[id].layerType === "icon") {
                    const iconSize = layers[id].iconSize;
                    const iconSizeUnits = layers[id].iconSizeUnits;
                    const iconLayerParams = {
                      pointType: "icon",
                      getIconColor: getColors,
                      getIcon: (d) => {
                        const iconProperty = layers[id].iconProperty;

                        if (
                          typeof iconProperty === "string" ||
                          iconProperty instanceof String
                        ) {
                          const iconType = d.properties[iconProperty];
                          const icon = layers[id].iconMap[iconType];
                          return icon ? icon : iconConfig["circle"];
                        } else {
                          return iconProperty;
                        }
                      },
                      getIconSize: iconSize ? iconSize : 8,
                      iconSizeUnits: iconSizeUnits ? iconSizeUnits : "pixels",
                      updateTriggers: {
                        ...sharedLayerParams.updateTriggers,
                        getIconColor: layers[id].colorValue,
                      },
                    };

                    newLayer = new GeoJsonLayer({
                      ...sharedLayerParams,
                      ...iconLayerParams,
                      parameters: {
                        depthTest: false,
                      },
                    });
                  }

                  // Add new layer to new layers list
                  resolve(newLayer);
                  break;
              }
            });
        })
      );
    }
  });

  return newLayerPromises;
}

const createNgwosProjectSitesLayer = ({
  aoiState,
  dataState,
  dispatch,
  filteredData,
  getData,
  handlePointLayerEvent,
  layer,
  layerActions,
}) => {
  // get names of selected basins, then get basins and filter by basin name
  const selectedIds = aoiState.selection?.selectedIds;
  const selectedBasinNames = Object.keys(aoiBasinMap).filter((basin) =>
    selectedIds.includes(
      aoiBasinMap[basin].find((b) => b.category === "ngwos-basin").id
    )
  );
  const selectedBasinIds = dataState.basins.data
    ?.filter((basin) => selectedBasinNames.includes(basin.name))
    .map((basin) => basin.id);

  filteredData = [...filteredData]
    .map((d) => ({
      type: "Feature",
      id: d.id,
      properties: d,
      geometry: {
        type: "Point",
        coordinates: [parseFloat(d.longitude), parseFloat(d.latitude)],
      },
    }))
    // filter out bad/missing coordinates
    .filter(
      (d) =>
        typeof d.geometry.coordinates[0] === "number" &&
        typeof d.geometry.coordinates[1] === "number"
    );

  // filter to basin
  if (selectedBasinIds?.length > 0)
    filteredData = [...filteredData].filter((d) => {
      const projects = d.properties.projects;
      if (!projects || projects.length === 0) {
        return false;
      }
      return projects
        .map((p) => p.basin_id)
        .some((basin_id) => selectedBasinIds.includes(basin_id));
    });

  // TODO: filter to project

  // initialize new layer
  const colorValue = layer.colorValue;
  const colorByProperty = layer.properties.find((p) => colorValue === p.id);

  // update point count
  dispatch(
    layerActions.updatePointCount({
      id: layer.id,
      count: filteredData.length,
    })
  );

  // Build color map
  //
  // Check for values of `colorValue` property without an assigned color
  // and add a random color if needed
  let colorMap = colorByProperty.colorMap;
  let uniquePropertyValues = [
    ...new Set(
      filteredData
        .map((f) => {
          if (Array.isArray(f[colorValue])) {
            return f.properties[colorValue].map((f) => f);
          } else {
            if (f.properties[colorValue])
              return (
                f.properties[colorValue].name ||
                f.properties[colorValue].name.id ||
                f.properties[colorValue]
              );
            return undefined;
          }
        })
        .flat()
        .filter((val) => val)
    ),
  ];
  const unassignedValues = uniquePropertyValues.filter(
    (val) => !Object.keys(colorMap).includes(val)
  );

  if (unassignedValues.length > 0) {
    unassignedValues.forEach((item) => {
      colorMap = {
        ...colorMap,
        [item]: { hex: randomColor(), opacity: 0.8 },
      };
    });

    // Update colorMap state
    dispatch(
      layerActions.updateColorMap({
        layerId: layer.id,
        propertyId: colorByProperty.id,
        colorMap,
      })
    );
  }

  // define function to pass to GeoJsonLayer
  const getColors = (f) => {
    const colorValue = layer.colorValue;
    let hexColor, rgbArray;

    // assign a default color if value
    if (!f.properties[colorValue]) {
      hexColor = "#808080";
      rgbArray = hexRgb(hexColor, { format: "array" });
      rgbArray[3] = Math.floor(0.8 * 255);
      return rgbArray;
    }
    let featureValue =
      f.properties[colorValue].name ||
      f.properties[colorValue].name.id ||
      f.properties[colorValue];

    // Handle case where featureValue is array
    if (Array.isArray(featureValue)) {
      // CASE: single value
      if (featureValue.length === 1) {
        featureValue = featureValue[0];
        hexColor = colorMap[featureValue].hex;
        rgbArray = hexRgb(hexColor, { format: "array" });
        rgbArray[3] = Math.floor(colorMap[featureValue].opacity * 255);
      }
      // CASE: multiple values
      else if (featureValue.length > 1) {
        const multipleValuesMap = colorByProperty.hasOwnProperty(
          "arrayMultipleValuesColor"
        )
          ? colorByProperty.arrayMultipleValuesColor
          : null;
        if (multipleValuesMap) {
          hexColor = multipleValuesMap.hex;
          rgbArray = hexRgb(hexColor, { format: "array" });
          rgbArray[3] = Math.floor(multipleValuesMap.opacity * 255);
        } else {
          hexColor = "#808080";
          rgbArray = hexRgb(hexColor, { format: "array" });
          rgbArray[3] = Math.floor(0.8 * 255);
        }
      }
      // CASE: no data
      else {
        hexColor = "#808080";
        rgbArray = hexRgb(hexColor, { format: "array" });
        rgbArray[3] = Math.floor(0.8 * 255);
      }
    }
    // if not array, featureValue type assumed to be string
    else {
      hexColor = colorMap[featureValue].hex;
      rgbArray = hexRgb(hexColor, { format: "array" });
      rgbArray[3] = Math.floor(colorMap[featureValue].opacity * 255);
    }
    return rgbArray;
  };

  // Parameterize layer
  //
  // Parameters shared across all layer types
  const sharedLayerParams = {
    id: layer.id,
    data: filteredData,
    extruded: true,
    filled: true,
    getElevation: 30,
    stroked: false,
    pickable: true,
    onHover: (info, event) =>
      handlePointLayerEvent(info, event, layer.id, "hover"),
    onClick: (info, event) =>
      handlePointLayerEvent(info, event, layer.id, "click"),
    updateTriggers: {
      filterData: aoiState.selection.id,
    },
  };

  // Icon layer specific parameters
  const iconSize = layer.iconSize;
  const iconSizeUnits = layer.iconSizeUnits;
  const iconLayerParams = {
    pointType: "icon",
    getIconColor: getColors,
    getIcon: (d) => {
      const iconProperty = layer.iconProperty;
      const iconType = d.properties[iconProperty];
      let icon;
      if (iconType) {
        icon = layer.iconMap[iconType.name];
      }
      return icon || iconConfig["circle"];
    },
    getIconSize: iconSize || 8,
    iconSizeUnits: iconSizeUnits || "pixels",
    updateTriggers: {
      ...sharedLayerParams.updateTriggers,
      getIconColor: layer.colorValue,
    },
  };

  return new GeoJsonLayer({
    ...sharedLayerParams,
    ...iconLayerParams,
    parameters: {
      depthTest: false,
    },
  });
};

// returns a string consisting of all the version hashes for each dataset
export function getDerivedDataState(dataState) {
  let derivedDataState = "";
  for (const [, value] of Object.entries(dataState)) {
    if (value.version) derivedDataState += value.version;
  }
  return derivedDataState;
}
